<template>
  <nuxt-link
    v-if="heroContainerLink && loaded"
    :to="cta.url"
    :class="heroClass"
    :style="backgroundImageSrc ? {
      backgroundImage: 'url(' + backgroundImageSrc + ')'
    } : {}"
  >
    <div class="l-container">
      <div class="c-hero__flex l-flex u-100-height">
        <div class="c-hero__content l-flex l-flex--column">
          <h1
            v-if="title"
            :class="titleClass"
          >
            {{ title }}
          </h1>
          <p
            v-if="subtitle"
            :class="subtitleClass"
          >
            {{ subtitle }}
          </p>
          <wysiwyg
            v-if="description && !plpHero.variant2"
            :html="description"
            paragraph-size="regular"
            :class="descriptionClass"
          />
        </div>
      </div>
    </div>
  </nuxt-link>
  <div
    v-else-if="loaded"
    :class="heroClass"
  >
    <nuxt-img
      v-if="backgroundImage && backgroundImage.src && (!isCollection || !plpHero.variant1)"
      class="c-hero__background-image"
      v-bind="$getImgOptions(backgroundImage, {
        alt: subtitle || 'Hero image',
        ...provider,
      })"
    />
    <img
      v-if="partnerLogo && partnerLogo.src"
      class="c-hero__logo-image c-hero__logo-image--mobile u-margin-bottom-large"
      :src="partnerLogo.src"
      alt="partnerLogo.alt || Partner logo"
    >
    <div :class="copyrightClass">
      {{ legalcopyrightText }}
    </div>
    <div class="l-container">
      <div :class="`${plpHero.variant1 && isCollection ? '' : 'c-hero__flex'} l-flex u-100-height`">
        <div class="c-hero__content l-flex l-flex--column">
          <nuxt-img
            v-if="partnerLogo && partnerLogo.src"
            class="c-hero__logo-image c-hero__logo-image--desktop u-margin-bottom-large"
            v-bind="$getImgOptions(partnerLogo, {
              alt: 'Partner Logo'
            })"
          />
          <!-- eslint-disable vue/no-v-html -->
          <h1 
            v-if="title"
            :class="titleClass"
            v-html="title"
          />
          <!-- eslint-enable vue/no-v-html -->
          <p
            v-if="subtitle.length > 0"
            :class="subtitleClass"
          >
            {{ subtitle }}
          </p>
          <wysiwyg
            v-if="description && !plpHero.variant2"
            :html="description"
            :class="descriptionClass"
          />
          <btn
            v-if="Object.keys(mobileCta).length > 0"
            v-bind="mobileCta"
            :type="btnType"
            :class="ctaClass"
          >
            {{ mobileCta.text }}
          </btn>
          <btn
            v-else-if="Object.keys(cta).length > 0"
            v-bind="cta"
            :type="btnType"
            :class="ctaClass"
          >
            {{ cta.text }}
          </btn>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <loader />
  </div>
</template>

<script>
import Btn from "../button/Btn";
import Loader from "../loader/Loader";
import Wysiwyg from "../wysiwyg/Wysiwyg";
import isMobile from '@mixins/isMobile';
import { UrlFilters } from '@mixins/cdnMixins'
import abTesting from '@mixins/abTesting';
import get from 'lodash/get'

export default {
  name: "Hero",
  components: {
    Btn,
    Loader,
    Wysiwyg,
  },
  mixins: [
    //this.isTabletLandscape is true screen width < 1200px
    //this.isTablet= is true screen width < 900px
    //this.isPhone is true screen width < 600px
    isMobile,
    abTesting,
    UrlFilters,
  ],
  props: {
    title: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    cta: {
      type: Object,
      default: () => ({}),
    },
    mobileCta: {
      type: Object,
      default: () => ({}),
    },
    customTheme: {
      type: String,
      default: ''
    },
    entireHeroClickable: {
      type: Boolean,
      default: false,
    },
    desktopBackgroundImage: {
      type: Object,
      default: () => ({
        src: '',
        alt: 'desktopBackgroundImage'
      })
    },
    mobileBackgroundImage: {
      type: Object,
      default: () => ({
        src: '',
        alt: 'mobileBackgroundImage'
      })
    },
    lifestyleAlignment: {
      type: String,
      default: ''
    },
    lifestyleMobileAlignment: {
      type: String,
      default: null
    },
    textColor: {
      type: String,
      default: 'base'
    },
    mobileTextColor: {
      type: String,
      default: ''
    },
    partnerLogo: {
      type: Object,
      default: () => ({})
    },
    legalcopyrightText: {
      type: String,
      default: ''
    },
    short: {
      type: Boolean,
      default: false
    },
    landingPageHero: {
      type: Boolean,
      default: false
    },
    textHiddenMobile: {
      type: Boolean,
      default: false
    },
    textHiddenDesktop: {
      type: Boolean,
      default: false
    },
    positionButtonToBottom: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      backgroundImage: null,
      btnType: null,
      mobileBtnType: null,
      textColorFiltered: '',
      windowWidth: 0,
    }
  },
  computed: {
    titleHtml() {
      if (this.title) {
        let t = '<h1>' + this.title + '</h1>';
        return t.replace(/#|™/g, '<sup>TM</sup>');
      }
      return '';
    },
    heroContainerLink() {
      if (this.cta && this.entireHeroClickable) {
        if (this.cta.url) {
          return true;
        }
      }
      return false;
    },
    provider() {
      return {
        preset: 'hero',
        sizes: 'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw 3xl:100vw'
      }
    },
    backgroundImageSrc() {
      const backgroundImage = this.backgroundImage
      const src = get(backgroundImage, 'src', '')
      return (src) ? this.$getImgUrl(src, { format: 'webp', quality: 100 }) : ''
    },
    copyrightClass() {
      let result = ['c-hero__copyright']
      if(this.short){
        result.push('c-hero__copyright--short');
      }
      return result;
    },
    titleClass() {
      let results = ['c-hero__title', 't-heading-2']
      results.push('t-heading-2--' + this.textColorFiltered)
      //console.log(this.description.length)
      if (this.subtitle.length > 0 || Object.keys(this.cta).length > 0 || this.description.length > 0) {
        results.push('u-margin-bottom-medium');
      }
      return results;
    },
    subtitleClass() {
      let results = ['c-hero__subtitle', 't-paragraph-large', 'u-margin-bottom-large']
      results.push('t-paragraph-large--' + this.textColorFiltered)
      return results;
    },
    descriptionClass() {
      let results = [];
      results.push('t-paragraph--' + this.textColorFiltered)
      if (this.cta) {
        results.push('u-margin-bottom-medium')
      }
      if ((this.textHiddenMobile && this.isMobile) || (this.textHiddenDesktop && !this.isMobile)) {
        results += ' u-hidden'
      }
      return results;
    },
    ctaClass() {
      let results = ['c-hero__cta']
      if (this.positionButtonToBottom && this.isMobile) {
        results.push('c-hero__cta--bottom')
      }
      return results;
    },
    loaded() {
      let result = true
      // for (let key of Object.keys(this.$props)) {
      //   if (
      //     this.$props[key] &&
      //     (this.$props[key]['default'] !== null)
      //   ) {
      //     result = true
      //     break
      //   }
      // }
      return result
    },
    lifestyleAlignmentFiltered() {
      const isTabletLandscape = this.isTabletLandscape
      const lifestyleMobileAlignment = this.lifestyleMobileAlignment
      const lifestyleAlignment = this.lifestyleAlignment
      if (isTabletLandscape && lifestyleMobileAlignment) {
        return lifestyleMobileAlignment
      } else {
        return lifestyleAlignment;
      }
    },
    heroClass() {
      const description = this.description
      const title = this.title
      const subtitle = this.subtitle
      const desktopBackgroundImage = this.desktopBackgroundImage
      const lifestyleAlignmentFiltered = this.lifestyleAlignmentFiltered
      const cta = this.cta
      const customTheme = this.customTheme
      const short = this.short
      const landingPageHero = this.landingPageHero
      let results = ['c-hero'];
      if (lifestyleAlignmentFiltered) {
        results.push("c-hero--lifestyle-" + lifestyleAlignmentFiltered);
      }
      if (desktopBackgroundImage && !title && !subtitle && !description && !cta) {
        results.push('c-hero--just-background-image');
      }
      if (customTheme) {
        results.push("theme-" + customTheme);
      }
      if (short) {
        results.push('c-hero--short-hero')
      }
      if (landingPageHero) {
        results.push('c-hero--landing-page-hero')
      }

      //plpHero A/B Testing
      if (this.plpHero.variant1 && this.isCollection) {
        results.push(`c-hero-variant1--${this.textColorFiltered}`)
      }
      if (this.plpHero.variant2 && this.isCollection) {
        results.push('c-hero-variant2')
        if(!this.isMobile && !this.isTabletLandscape && !this.isTablet) {
          results.push(`c-hero-variant2--${lifestyleAlignmentFiltered}-${this.textColorFiltered}`)
        }
      }
      //end A/B Testing
      return results;
    },
    isCollection() {
      let result = false
      if(this.$route && this.$route.fullPath.includes('/collections/')) {
        result = true
      }
      return result
    },
    plpHero() {
      const abTesting = this.abTesting
      let result = { default: true }
      if (abTesting) {
        const store = this.$store
        if (
          store &&
          store.state &&
          store.state.vwo &&
          store.state.vwo.plpHero
        ) {
          result = store.state.vwo.plpHero
        }
      }
      return result
    }
  },
  watch: {
    // make everything that's computed in this method reactive
    mobileBackgroundImage() {
      this.mobileDesktopUpdates()
    },
    desktopBackgroundImage() {
      this.mobileDesktopUpdates()
    },
    textColor() {
      this.mobileDesktopUpdates()
    },
    mobileTextColor() {
      this.mobileDesktopUpdates()
    },
    cta() {
      this.mobileDesktopUpdates()
    },
    mobileCta() {
      this.mobileDesktopUpdates()
    }
  },
  created() {
    try {
      window.addEventListener("resize", this.mobileDesktopUpdates,);
    } catch (err) {
      // console.log('window not loaded');
    }
  },
  mounted() {
    this.mobileDesktopUpdates();
    try{
      this.windowWidth = window.innerWidth
    } catch (err) {
      // console.log('window not loaded');
    }

    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth
    })
  },
  destroyed() {
    try {
      window.removeEventListener("resize", this.mobileDesktopUpdates);
    } catch (err) {
      // console.log('window not loaded');
    }
  },
  methods: {
    // preferably the data properties here would be computed
    mobileDesktopUpdates() {
      if (window.innerWidth <= 1200) {
        if (this.mobileBackgroundImage) {
          this.backgroundImage = this.mobileBackgroundImage;
        } else {
          this.backgroundImage = this.desktopBackgroundImage;
        }
        if (this.cta) {
          this.btnType = this.cta.type;
        }
        if (this.mobileCta) {
          this.btnType = this.mobileCta.type;
        }
        if (this.mobileTextColor) {
          this.textColorFiltered = this.mobileTextColor;
        } else {
          this.textColorFiltered = this.textColor;
        }
      } else {
        if (this.desktopBackgroundImage) {
          this.backgroundImage = this.desktopBackgroundImage;
        }
        if (this.cta) {
          this.btnType = this.cta.type;
        }
        this.textColorFiltered = this.textColor;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.c-hero {
  width: 100%;
  height: rem(600px);
  display: block;
  position: relative;
  @include themify($themes) {
    color: themed('foreground', 'white-text');
  }
  @include respond(tab-land) {
    height: 400px;
    background-position: top;
  }
  &__background-image {
    position: absolute;
    left: 0;
    z-index: -1;
    width: 100%;
    height: 100%;
    object-fit: cover;

    @include respond(tab-land) {
      height: 400px;
    }
  }
  &--short-hero {
    max-height: rem(400px);
    @include respond(tab-land) {
      max-height: unset;
      min-height: rem(750px);
    }
    @include respond(phone) {
      min-height: rem(750px);
    }
    @include respond(tab-port) {
      min-height: rem(750px);
    }
  }
  &--landing-page-hero {
    max-height: rem(200px);
    @include respond(tab-land) {
      max-height: rem(200px);
    }
  }
  &--just-background-image {
    @include respond(tab-land) {
      height: 400px;
    }
  }
  ::v-deep &__title {
    h1 {
      font-size: 5.2rem;
      font-weight: 300;
      line-height: 5.8rem;
      letter-spacing: 0.12rem;
    }
    sup {
      font-size: 0.4em;
    }
  }
  &__image {
    display: none;
    max-width: 100%;

    @include respond(tab-land) {
      display: inline-block;
    }
  }

  &__content {
    width: rem(420px);
    max-width: 100vw;
    @include respond(tab-land) {
      margin-top: 430px;
      text-align: center;
      align-items: center;
    }
  }
  &__logo-image {
    max-height: rem(100px);
    max-width: rem(250px);
    top: rem(50px);
    left: 10%;
    @include respond(tab-land) {
      margin:auto;
      position: absolute;
      max-height: rem(100px);
      top: rem(50px);
      margin-left: auto;
      margin-right: auto;
      left: 0;
      right: 0;
      text-align: center;
    }
    &--desktop {
      @include respond(tab-land) {
        display: none;
      }
    }
    &--mobile {
      display: none;
      top: rem(10px);
      max-height: rem(50px);
      @include respond(tab-land) {
        display: block;
      }
    }
  }
  &__copyright {
    position: absolute;
    right:rem(20px);
    top: 700px;
    @include respond(tab-land) {
      bottom: auto;
      top: 500px;
      color:$white;
    }
    @include respond(tab-port) {
      top: 500px;
      color:$white;
    }
    @media only screen and (max-width: 358px) {
      top: 520px;
      color:$white;
    }
  }
  &__copyright--short {
    position: absolute;
    right:rem(20px);
    top: 500px;
    @include respond(tab-land) {
      bottom: auto;
      top: 500px;
      color:$white;
    }
    @include respond(tab-port) {
      top: 500px;
      color:$white;
    }
    @media only screen and (max-width: 358px) {
      top: 520px;
      color:$white;
    }
  }

  &__cta {
    &--bottom {
      position: absolute;
      bottom: 30px;
    }
  }

  //variant Lifestyle left overrides
  &--lifestyle-left.c-hero {
    text-align: left;
    @include respond(tab-land) {
      padding-bottom: rem(40px);
      // padding-top: 430px;
      background-size: auto 400px;
      height: auto;
      @include themify($themes) {
        color: themed('foreground');
      }
    }
    .c-hero {
      &__flex {
        justify-content: flex-end;
        @include respond(tab-land) {
          justify-content: center;
        }
      }

      &__content {
        align-items: flex-start;
        @include respond(tab-land) {
          margin-top: 430px;
          align-items: center;
        }
      }
    }
  }

  //variant Lifestyle right overrides
  &--lifestyle-right {
    text-align: left;

    @include respond(tab-land) {
      padding-bottom: rem(40px);
      // padding-top: 430px;
      background-size: auto 400px;
      height: auto;
      text-align: center;
      @include themify($themes) {
        color: themed('foreground');
      }
    }

    .c-hero {
      &__flex {
        justify-content: flex-start;
        @include respond(tab-land) {
          justify-content: center;
        }
      }

      &__content {
        align-items: flex-start;
        @include respond(tab-land) {
          align-items: center;
        }
      }
    }
  }
}

.c-hero-variant1 {
  &--base {
    .c-hero__content {
      text-align: center;
      justify-content: center;
      align-items: center!important;
      margin-top: rem(40px)!important;
    }
    @include themify($themes) {
      background-color: themed('background','xx-light');
    }
  }
  
  &--white {
    .c-hero__content {
      text-align: center;
      justify-content: center;
      align-items: center!important;
      margin-top: rem(40px)!important;
    }
    @include themify($themes) {
      background-color: themed('foreground','base');
    }
  }
}

.c-hero-variant2 {
  @include respond(phone) {
    position:relative;
    overflow:hidden;
  }
  @include respond(tab-land) {
    position:relative;
    overflow:hidden;
  }
  @include respond(tab-port) {
    position:relative;
    overflow:hidden;
  }

  .c-hero__content {
    z-index: 1;
    &:before {
      content:"";
      position:absolute;
      @include respond(phone) {
        width: 150vw;
        height: 400px;
        left: -27%;
        top: -1.3%;
        z-index: -1;
        border-radius: 0 0 100% 100%;
        box-shadow: 0 0px 0 390px #fafbfb;
      }
      @include respond(tab-land) {
        width: 150vw;
        height: 400px;
        left: -27%;
        top: -1.3%;
        z-index: -1;
        border-radius: 0 0 100% 100%;
        box-shadow: 0 0px 0 390px #fafbfb;
      }
      @include respond(tab-port) {
        width: 150vw;
        height: 400px;
        left: -27%;
        top: -1.3%;
        z-index: -1;
        border-radius: 0 0 100% 100%;
        box-shadow: 0 0px 0 390px #fafbfb;
      }
    }
  }
  &--left {
    &-base {
      .c-hero__content:before {
        content:"";
        position:absolute;
        @include themify($themes) {
          background-color: themed('background','xx-light');
        }
        clip-path: circle(80% at right center);
        top:0;
        left:30%;
        right:0;
        bottom:0;
        z-index:-1;
      }
    }
    &-white {
      .c-hero__content:before {
        content:"";
        position:absolute;
        @include themify($themes) {
          background-color: themed('foreground','base');
        }
        clip-path: circle(80% at right center);
        top:0;
        left:30%;
        right:0;
        bottom:0;
        z-index:-1;
      }
    }
  }
  &--right {
    &-base {
      .c-hero__content:before {
        content:"";
        position:absolute;
        @include themify($themes) {
          background-color: themed('foreground','xx-light');
        }
        clip-path: circle(80% at left center);
        top:0;
        left:0;
        right:30%;
        bottom:0;
        z-index:-1;
      }
    }
    &-white {
      .c-hero__content:before {
        content:"";
        position:absolute;
        @include themify($themes) {
          background-color: themed('foreground','base');
        }
        clip-path: circle(80% at left center);
        top:0;
        left:0;
        right:30%;
        bottom:0;
        z-index:-1;
      }
    }
  }
}

</style>
